import type { RouteRecordRaw } from 'vue-router';
import { isInDocoiApp } from '@h5-monorepo/shared/utils/app';

const gardenAccessGuard: RouteRecordRaw['beforeEnter'] = (to) => {
  const inApp = isInDocoiApp();
  const isGardenMain = to.name === 'gardenMain';
  const isGardenFriend = to.name === 'gardenFriend';
  const isGardenShare = to.name === 'gardenShare';

  const friendUidParam = Array.isArray(to.params?.uid) ? to.params.uid[0] : to.params?.uid;
  const passthroughQuery = {
    ...to.query,
    ...(isGardenFriend && friendUidParam ? { fromUid: String(friendUidParam) } : {})
  };

  // App 外：主态/好友态 => 分享页
  if (!inApp && (isGardenMain || isGardenFriend)) {
    return {
      name: 'gardenShare',
      query: passthroughQuery,
      hash: to.hash
    };
  }

  // App 内：分享页 => 主态
  if (inApp && isGardenShare) {
    return {
      name: 'gardenMain',
      query: passthroughQuery,
      hash: to.hash
    };
  }

  return true;
};

// App 外打开长文详情页 => 重定向到外部分享页
const longEssayAccessGuard: RouteRecordRaw['beforeEnter'] = (to) => {
  if (!isInDocoiApp()) {
    return {
      name: 'DynamicShare',
      params: { dynamicid: to.params.id as string },
      query: to.query
    };
  }
  return true;
};

const routes: RouteRecordRaw[] = [
  {
    path: '/immerse/mall',
    component: () => import('@/views/mall/index.vue'),
    children: [
      // {
      //   path: '',
      //   name: 'mallHome',
      //   component: () => import('@/views/mall/home.vue'),
      //   meta: {
      //     title: '商城',
      //     keepAlive: true,
      //     pageInfo: {
      //       PageID: 11400,
      //       PageName: '平台商城首页'
      //     }
      //   }
      // },
      {
        path: '',
        name: 'mallHome',
        component: () => import('@/views/mall/home.vue'),
        meta: {
          title: '商城',
          keepAlive: true,
          pageInfo: {
            PageID: 11401,
            PageName: '平台商城商品列表页'
          }
        }
      },
      {
        path: 'goodsDetails',
        name: 'mallGoodsDetails',
        component: () => import('@/views/mall/goodsDetail.vue'),
        meta: {
          title: '商品详情',
          immersionPage: true,
          pageInfo: {
            PageID: 11402,
            PageName: '平台商城商品详情页'
          }
        }
      },
      {
        path: 'myOrders',
        name: 'mallMyOrders',
        component: () => import('@/views/mall/order/list.vue'),
        meta: {
          title: '我的订单',
          pageInfo: {
            PageID: 11403,
            PageName: '我的订单页'
          }
        }
      },
      {
        path: 'myOrders/detail',
        name: 'mallMyOrdersDetail',
        component: () => import('@/views/mall/order/detail.vue'),
        meta: {
          title: '订单详情',
          pageInfo: {
            PageName: '订单详情页'
          }
        }
      },
      {
        path: 'address',
        name: 'mallAddress',
        component: () => import('@/views/mall/address/list.vue'),
        meta: {
          title: '我的地址',
          keepAlive: true,
          pageInfo: {
            PageName: '我的地址页'
          }
        }
      },
      {
        path: 'address/add',
        name: 'mallAddressAdd',
        component: () => import('@/views/mall/address/add.vue'),
        meta: {
          title: '添加收货地址',
          pageInfo: {
            PageName: '添加收货地址页'
          }
        }
      }
    ]
  },

  {
    path: '/immerse/luckyDraw',
    name: 'luckyDraw',
    component: () => import('@/views/h5/luckyDraw.vue')
  },
  {
    path: '/immerse/platformTask',
    name: 'platformTask',
    component: () => import('@/views/h5/platformTask.vue'),
    meta: {
      title: '任务中心',
      immersionPage: true,
      keepAlive: true,
      pageInfo: {
        PageID: 11300,
        PageName: '平台任务页'
      }
    }
  },
  {
    path: '/immerse/recruitActivity/setGroup',
    name: 'immerse_recruitActivitySetGroup',
    component: () => import('@/views/games/pullNew/setSmallGroup.vue'),
    meta: {
      title: '选择群聊'
    }
  },
  {
    path: '/immerse/recruitActivity',
    name: 'immerse_recruitActivity',
    component: () => import('@/views/games/pullNew/index.vue'),
    meta: {
      title: '砍价0元购',
      keepAlive: true
    }
  },
  {
    path: '/immerse/backpack',
    name: 'backpack',
    component: () => import('@/views/h5/backpack.vue'),
    meta: {
      title: '背包',
      immersionPage: true,
      pageInfo: {
        PageID: 10704,
        PageName: '背包页'
      }
    }
  },
  {
    path: '/immerse/new-year-activity',
    name: 'newYearActivity',
    component: () => import('@/views/h5/newYear.vue'),
    meta: {
      title: '春节福运到',
      immersionPage: true
    }
  },
  {
    path: '/immerse/redeemCode',
    name: 'redeemCode',
    component: () => import('@/views/h5/redeemCode.vue'),
    meta: {
      title: '兑换码',
      immersionPage: true,
      userSource: 'redeemCode',
      pageInfo: {
        PageID: 10311,
        PageName: '礼包码页'
      }
    }
  },
  {
    path: '/immerse/fameHall/:server_id/:flag_user_id?/:tab_type?',
    name: 'fameHall',
    component: () => import('@/views/h5/fameHall/index.vue'),
    meta: {
      title: '名人堂',
      userSource: 'fameHall'
    }
  },
  {
    path: '/immerse/game/:id',
    name: 'discover',
    component: () => import('@/views/discover/index.vue'),
    meta: {
      title: '游戏详情'
    }
  },
  {
    path: '/immerse/social-persona',
    name: 'socialPersona',
    component: () => import('@/views/h5/socialPersona/index.vue'),
    redirect: '/immerse/social-persona/home',
    children: [
      {
        path: 'home',
        name: 'socialPersonaHome',
        component: () => import('@/views/h5/socialPersona/home.vue'),
        meta: {
          title: '解锁身份'
        }
      },
      {
        path: 'set',
        name: 'socialPersonaSet',
        component: () => import('@/views/h5/socialPersona/setUserInfo.vue'),
        meta: {
          title: '生成人设资料卡'
        }
      },
      {
        path: 'result',
        name: 'socialPersonaResult',
        component: () => import('@/views/h5/socialPersona/personaResult.vue'),
        meta: {
          title: '人设资料卡'
        }
      },
      {
        path: 'mbti-test',
        name: 'socialPersonaMbtiTest',
        component: () => import('@/views/h5/socialPersona/mbtiTest.vue'),
        meta: {
          title: 'MBTI性格测试'
        }
      },
      {
        path: 'mbti-test-result',
        name: 'socialPersonaMbtiTestResult',
        component: () => import('@/views/h5/socialPersona/mbtiTestResult.vue'),
        meta: {
          title: 'MBTI性格测试结果'
        }
      },
      {
        path: 'share/:uid',
        name: 'socialPersonaShare',
        component: () => import('@/views/h5/socialPersona/share.vue'),
        meta: {
          title: '人设资料卡分享'
        }
      }
    ]
  },
  {
    path: '/immerse/annual-report/2025',
    name: 'annualReport2025',
    component: () => import('@/views/annualReport/2025/index.vue'),
    meta: {
      title: '2025年度报告'
    }
  },
  {
    path: '/immerse/annual-report/2025/share',
    name: 'annualReport2025Share',
    component: () => import('@/views/annualReport/2025/share.vue'),
    meta: {
      title: '2025年度报告分享'
    }
  },
  {
    path: '/immerse/gachaMachine/',
    name: 'gachaMachine',
    component: () => import('@/views/annualReport/2025/gachaMachine.vue'),
    meta: {
      title: '扭蛋机'
    }
  },
  {
    path: '/immerse/query-recharge/:game_id',
    name: 'queryRecharge',
    component: () => import('@/views/h5/queryRechargeAmount.vue'),
    meta: {
      title: '查询累充金额'
    }
  },
  {
    path: '/immerse/adwheel/:id',
    name: 'adwheel',
    component: () => import('@/views/h5/advertisingWheel/index.vue'),
    meta: {
      title: '转盘'
    }
  },
  {
    path: '/immerse/adwheelDialog/:id',
    name: 'adwheelDialog',
    component: () => import('@/views/h5/advertisingWheel/indexDialog.vue'),
    meta: {
      title: '转盘'
    }
  },
  {
    path: '/immerse/sf/active',
    name: 'springFestivalActive',
    component: () => import('@/views/springFestival/2026/index.vue'),
    meta: {
      title: '集五福'
    }
  },
  {
    path: '/immerse/sf/collect-blessing',
    name: 'springFestivalCollectBlessing',
    component: () => import('@/views/springFestival/2026/collectBlessing.vue'),
    meta: {
      title: '集五福-分享页'
    }
  },
  {
    path: '/immerse/sbti',
    name: 'sbtiS6',
    component: () => import('@/views/sbti/s6/index.vue'),
    meta: {
      title: 'SBTI・无限轮回版 人格测试'
    }
  },
  {
    path: '/immerse/t5Sbti',
    name: 't5Sbti',
    component: () => import('@/views/sbti/t5/index.vue'),
    meta: {
      title: '向僵尸开炮・末日变异档案'
    }
  },
  {
    path: '/immerse/spti',
    name: 'spti',
    component: () => import('@/views/sbti/spti/index.vue'),
    meta: {
      title: '魔植SPTI・人格测试'
    }
  },
  //互动抽奖页
  {
    path: '/immerse/interactive-lottery/:lotteryId',
    name: 'interactiveLottery',
    component: () => import('@/views/interactiveLottery/index.vue'),
    meta: {
      title: '互动抽奖'
    }
  },
  {
    path: '/immerse/garden',
    name: 'gardenMain',
    component: () => import('@/views/garden/index.vue'),
    beforeEnter: [gardenAccessGuard],
    meta: {
      title: '花园魔植大乱逗',
      immersionPage: true
    }
  },
  {
    path: '/immerse/garden/friend/:uid',
    name: 'gardenFriend',
    component: () => import('@/views/garden/friendGarden.vue'),
    beforeEnter: [gardenAccessGuard],
    meta: {
      title: '好友花园',
      immersionPage: true
    }
  },
  {
    path: '/immerse/garden/share',
    name: 'gardenShare',
    component: () => import('@/views/garden/share.vue'),
    beforeEnter: [gardenAccessGuard],
    meta: {
      title: '花园分享'
    }
  },
  //分享图
  {
    path: '/immerse/md/active',
    name: 'mayDayFestivalActive',
    component: () => import('@/views/mayDayFestival/2026/index.vue'),
    meta: {
      title: '「牛马大作战」· 集卡赢游戏大礼包'
    }
  },
  {
    path: '/immerse/md/collect-blessing',
    name: 'mayDayFestivalCollectBlessing',
    component: () => import('@/views/mayDayFestival/2026/collectBlessing.vue'),
    meta: {
      title: '「牛马大作战」-分享页'
    }
  },
  {
    path: '/immerse/growthSystem/index/:serverId',
    name: 'growth_system_index',
    component: () => import('@/views/growthSystem/index.vue'),
    meta: {
      title: '成长体系'
    }
  },
  {
    path: '/immerse/growthSystem/dataView/:serverId',
    name: 'growth_system_data_view',
    component: () => import('@/views/growthSystem/dataView.vue'),
    meta: {
      title: '详情'
    }
  },
  {
    path: '/immerse/growthSystem/findFriend/:serverId',
    name: 'growth_system_find_friend',
    component: () => import('@/views/growthSystem/findFriend.vue'),
    meta: {
      title: '找钓友'
    }
  },
  {
    path: '/immerse/c6/team',
    component: () => import('@/views/teamHall/index.vue'),
    children: [
      {
        path: '',
        name: 'c6_team_hall',
        component: () => import('@/views/teamHall/home.vue'),
        meta: {
          keepAlive: true
        }
      },
      {
        path: 'detail/:id',
        name: 'c6_team_detail',
        component: () => import('@/views/teamHall/detail.vue')
      },
      {
        path: 'share-detail/:id',
        name: 'c6_team_share_detail',
        component: () => import('@/views/teamHall/shareDetail.vue')
      },
      {
        path: 'create',
        name: 'c6_team_create',
        component: () => import('@/views/teamHall/create.vue')
      },
      {
        path: 'publish-record',
        name: 'c6_team_publish_record',
        component: () => import('@/views/teamHall/publishRecord.vue'),
        meta: {
          keepAlive: true
        }
      }
    ]
  },
  {
    path: '/immerse/memeChallenge',
    meta: { layout: 'share' },
    children: [
      {
        path: ':server_id',
        name: 'immerse_memeChallenge',
        component: () => import('@/views/memeChallenge/index.vue'),
        meta: {
          title: '整活挑战'
        }
      },
      {
        path: 'detail/:challenge_id/:task_id',
        name: 'immerse_memeChallenge_detail',
        component: () => import('@/views/memeChallenge/memeDetail.vue'),
        meta: {
          title: '挑战详情'
        }
      },
      {
        path: 'startChallenge/:challenge_id/:task_id',
        name: 'immerse_memeChallenge_start',
        component: () => import('@/views/memeChallenge/startChallenge.vue'),
        meta: {
          title: '发起挑战'
        }
      },
      {
        path: 'challengeSpinner/:challenge_id/:server_id',
        name: 'immerse_memeChallenge_spinner',
        component: () => import('@/views/memeChallenge/challengeSpinner.vue'),
        meta: {
          title: '整活积分转盘'
        }
      },
      {
        path: 'submitChallenge/:server_id',
        name: 'immerse_memeChallenge_submit',
        component: () => import('@/views/memeChallenge/submitChallenge.vue'),
        meta: {
          title: '挑战投稿'
        }
      },
      {
        path: 'challengeSubmitReward/:server_id',
        name: 'immerse_memeChallenge_submitReward',
        component: () => import('@/views/memeChallenge/challengeSubmitReward.vue'),
        meta: {
          title: '整活投稿奖励'
        }
      },
      {
        path: 'challengeRewardRecord/:challenge_id/:server_id',
        name: 'immerse_memeChallenge_rewardRecord',
        component: () => import('@/views/memeChallenge/rewardRecord.vue'),
        meta: {
          title: '获奖记录'
        }
      }
    ]
  },
  {
    path: '/immerse/longEssay/:id',
    name: 'longEssay_page',
    component: () => import('@/views/longEssay/page.vue'),
    beforeEnter: [longEssayAccessGuard],
    meta: {
      title: '动态详情'
    }
  },
  {
    path: '/immerse/socialSquare/checkIn',
    name: 'socialSquare_checkIn',
    component: () => import('@/views/h5/socialSquare/checkIn/index.vue'),
    meta: {
      title: '七日签到',
      layout: 'share'
    }
  }
];

export default routes;
